import { LitElement, html, css, PropertyValues, nothing } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { AlarmType } from '../../../src/services/types/fleet-alarms-service.types';
import { FormatDateToLTFormat, formatToTitleCase, prettyTimelapsed } from '../../../src/utils/helpers';
import { compassRose } from '../../../src/utils/weather-utils';
import { VesselProperties } from './types';
import { differenceInHours } from "date-fns";
import { VesselsTrackingModel } from "../../../src/models/vessels-tracking-model";
import { VesselPositionData } from "../../../src/services/types/position-service.types";
import { PositionsService } from '../../../src/services/positions-service';
import { VesselsModel } from '../../../src/models/vessels-model';
import { VesselVoyageVersion } from '../../voy-global-filters/src/types';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { PlanSource, VoyagePlansModel } from '../../../src/models/voyage-plans-model';
import { VoyagePlan } from '../../../src/models/vessel-planning-model';
import { WEATHER_LIMIT_VALUES } from '../../../src/utils/weather-limits';

@customElement('voy-map-vessel-tooltip')
export class VoyMapVesselTooltip extends LitElement {
    @property({type: Boolean}) visible: boolean = false; 
    @property({type: Object}) vesselProperties?: VesselProperties; 
    @property({type: String}) private _terminalCode?: string; 
    @property({type: Number}) private _arrDate?: number | null; 
    @property({ attribute: false }) private _latestPosition: 
    VesselPositionData|undefined;
    @property({ type: Number }) public imo?: number;
    @property({ attribute: false }) private _planData?: VoyagePlan | null;
   
    constructor() {
        super();
        this.imo = undefined;
        this._planData = null;
    }

    connectedCallback() {
        super.connectedCallback();
        VesselsTrackingModel?.addEventListener('tracking-updated', this._handlePositionUpdate);
    }
    disconnectedCallback() {
        VesselsTrackingModel!.removeEventListener('tracking-updated', this._handlePositionUpdate);
    }
    static get styles() {
        return [ 
            css`
                .tooltip {
                    background: var(--neutrals-grey-02, gray);
                    display: none;
                }
                .tooltip[visible] {
                    display: block;
                    padding: 16px;
                    border-radius: 5px;
                    position: relative;
                    bottom: 12px;
                }
                p {
                    margin: 0;
                    padding:0;
                }
                .vessel-title {
                    color: white;
                    font-weight: bold;
                    font-size: 16px;
                    line-height: 16px;
                    font-family: MaerskText;
                }
                .vessel-type {
                    margin-bottom: 10px;
                    margin-top: -2px;
                }
                .vessel-info{
                    color: white;
                    font-size: 12px;
                    line-height: 16px;
                    font-family: MaerskText;
                }
                .tooltip:after{
                    top: 100%;
                    border: solid transparent;
                    content: " ";
                    height: 0;
                    width: 0;
                    position: absolute;
                    pointer-events: none;
                }
                .tooltip:after {
                    border-top-color: var(--neutrals-grey-02, gray);
                    border-width: 10px;
                    left: calc(50% - 10px);
                }
                .box {
                    display: flex;
                    box-sizing: border-box;
                    justify-content: space-between;
                    align-items: center;
                }

                .bg {
                    display: flex;
                    justify-content: center;
                    border-radius: 20px;
                    font-size: 10px;
                    margin-left: 16px;
                }
                .gray-bg {
                    background-color: #CFCFCF;
                    width: 7em;
                }

                .red-bg{
                    background-color: #E9B2B7;
                    width: 4em;
                }

                .green-bg {
                    background-color: #C5E5C2;
                    width: 4em;
                } 

                .premium-icon {
                    margin-left: -10px;
                }

                .weather-text-container{
                 padding-top: 10px;
                 padding-right: 10px;
                }

                .padding {
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    gap: 40px;
                }
            
        `];
    }
    private  async _handlePositionUpdate() {
        if (!this.vesselProperties?.vesselInfo?.imoNo) {
            this._latestPosition = undefined;
            return;
        };
        const currentPosition = await PositionsService.currentPosition_VesselVoyage([this.vesselProperties!.vesselInfo.imoNo]);
        this._latestPosition = currentPosition[0]
        this.imo = this.vesselProperties?.vesselInfo?.imoNo;
    }
    private async _getSchedule() {
        const schedules = await VoyageSchedulesModel?.getScheduleFor(this.vesselProperties!.vesselInfo.imoNo);
        const schedule = schedules?.find(schedule => schedule.active);

        if( schedule ){
            this._terminalCode = schedule.arrivalTerminalCode;
            this._arrDate = schedule.eta;
        } else {
            this._arrDate = null
        }
    }

    private _getAlarmText() {
        const sortedAlarmsMap = new Map([
            [AlarmType.Stoppage, {text: "Stoppage", active: false}],
            [AlarmType.Speed, {text: "Speed", active: false}],
            [AlarmType.Crosstrack, {text: "CrossTrack", active: false}],
            [AlarmType.NoPlan, {text: "No Plan", active: false}],
            [AlarmType.HeavyWeather, {text: "Heavy Weather", active: false}],
            [AlarmType.ParametricRoll, {text: "Parametric Roll", active: false}],
        ]);

        const alarms = this.vesselProperties?.vesselInfo.alarms!;
        let alarmText = "";
        
        for (let i = 0; i < alarms.length; i++) {
            sortedAlarmsMap.get(alarms[i].alarmType)!.active = true;
        }

        for (const alarm of sortedAlarmsMap.values()) {
            if (alarm.active) {
                if (alarmText === "") {
                    alarmText = `${alarm.text}`;
                    continue;
                }
                alarmText += `, ${alarm.text}`;
            }
        }
        return alarmText;
    }

    async fetchPlanData() {
        if (this.imo) {
            try {
                const plans = await VoyagePlansModel!.getPlans(this.imo);
                if (plans?.has(PlanSource.VOYAGE_PLANNING)) {
                    this._planData = plans.get(PlanSource.VOYAGE_PLANNING)
                } else if (plans?.has(PlanSource.ECOVOYAGE)) {
                    this._planData = plans.get(PlanSource.ECOVOYAGE)
                } else if (plans?.has(PlanSource.EXCEL_PLAN)) {
                    this._planData = plans.get(PlanSource.EXCEL_PLAN)
                } else {
                    this._planData = plans?.get(PlanSource.PASSAGE_PLAN)
                }

                VoyagePlansModel!.syncLatestNoPlanReminder(this.imo);
            } catch (error) {
                console.error('Error fetching plans:', error);
            }
        } else {
            console.error('IMO is undefined');
        }
    }

    private getWeatherText(){
        this.fetchPlanData();
        const weatherLimits = this._planData?.routes[0]?.route_info?.calculationInput?.weatherLimits;

        if (!weatherLimits) {
            return html``;
          }
                
        const weatherLimitOrder = WEATHER_LIMIT_VALUES;

        const weatherTexts = weatherLimitOrder
        .filter(item => !item.key.includes('Period' || 'Visibility' || 'Icing'))
        .map(item =>{
            const key = item.key;
            const label = item.label;
            const avoid = (weatherLimits[key as keyof typeof weatherLimits]).avoid;
            const warn = (weatherLimits[key as keyof typeof weatherLimits]).warn;
            const avoidValue = avoid ? avoid : '--';
            const warnValue = warn ? warn : '--';

            if(warn || avoid){
                return html`
                <div class="vessel-info padding">
                    <span>${label}</span>
                    <span>${warnValue}/${avoidValue}</span>
                </div>`;
            }
            return nothing;
        })
    
        return html`${weatherTexts}`;
    }

    updated(changedProperties:PropertyValues) {
        if (changedProperties.has("vesselProperties")) {
            this._getSchedule();
            this._handlePositionUpdate()
        }
    }
   
    render() {
        const heading = this.vesselProperties?.vesselInfo.heading && `${this.vesselProperties?.vesselInfo.heading}° (${compassRose(this.vesselProperties.vesselInfo.heading)})`;
        const timeDiff = this.vesselProperties ? differenceInHours(new Date(), new Date(this.vesselProperties?.vesselInfo.timestamp)) : null;
        const isPremium = VesselsModel?.maerskVessels.get(this.vesselProperties?.vesselInfo.imoNo!)?.vesselVersion === VesselVoyageVersion.Premium;
        let className = "bg green-bg";
    
         if (timeDiff && timeDiff >= 4) {
            className = "bg red-bg"
        } else if(timeDiff && timeDiff < 4) {
            className = "bg green-bg"
        }

        let formattedDate;
        if(this._terminalCode && this._arrDate) {
            const zone = TerminalsModel!.getTerminal(this._terminalCode)?.olsonTimezone;
            const date = zone && FormatDateToLTFormat(this._arrDate, zone);
            formattedDate = `ETA GSIS ${this._terminalCode}: ${date}`
        }
        return html`
            <div class="tooltip" ?visible=${this.visible}>
                <div class="box">
                    <p class="vessel-title"><span class="premium-icon" ?hidden=${!isPremium}>${SvgIcons.customPremiumVessel}</span>${this.vesselProperties?.vesselInfo.name}</p>
                    <div>${this.vesselProperties ? html` <div class=${className}>${prettyTimelapsed(this._latestPosition?.timestamp)} </div>` : html`<div class="gray-bg">No position</div>` }</div>
                </div>
                ${html`<p class="vessel-info vessel-type">${this.vesselProperties?.vesselType!}</p>`}
                ${this.vesselProperties?.vesselInfo.speed ? html`<p class="vessel-info">${this.vesselProperties?.vesselInfo.speed.toFixed(1)} kt ${heading}</p>`: nothing}
                ${formattedDate ? html`<p class="vessel-info"> ${formattedDate}</p>` : nothing}
                ${this.vesselProperties?.vesselInfo.alarms.length ? html`<p class="vessel-info">Alarm: ${formatToTitleCase(this._getAlarmText())}</p>` : nothing}
                ${isPremium ? html`<div class="weather-text-container">${this.getWeatherText()} </div>` : nothing}
            </div>
        `;
    }
};
